import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, doc, onSnapshot } from 'firebase/firestore';
import { functions, storage, db } from '../lib/firebase';
import { ref, uploadBytes } from 'firebase/storage';
import { CloudUpload } from 'lucide-react';

interface EntityStatus {
  status: 'pending' | 'uploading' | 'processing' | 'complete' | 'failed';
  excelFileName?: string;
  recordCount?: number;
  error?: string;
}

interface MultiEntityUploadRunStatus {
  runId: string;
  companyId: string;
  month: string;
  runNumber: 1 | 2 | 3;
  expectedEntities: string[];
  entityStatus: Record<string, EntityStatus>;
  status: 'incomplete' | 'processing' | 'complete' | 'partial_complete';
  completedEntities: string[];
}

interface MultiEntityUploaderProps {
  companyId: string;
  expectedEntities: string[];
  entityNames: Record<string, string>;
}

const MultiEntityUploader: React.FC<MultiEntityUploaderProps> = ({
  companyId,
  expectedEntities,
  entityNames
}) => {
  // Run selector state
  const [selectedMonth, setSelectedMonth] = useState<string>(
    `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`
  );
  const [selectedRun, setSelectedRun] = useState<1 | 2 | 3>(1);

  // Upload state
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Run status
  const [runStatus, setRunStatus] = useState<MultiEntityUploadRunStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);

  // Entity mappings from Firestore (loaded once)
  const [entityMappings, setEntityMappings] = useState<Array<{ code: string; officeName: string; legalEntityName: string }>>([]);
  const [loadingMappings, setLoadingMappings] = useState(true);

  // Generate runId for status lookup
  const runId = `run_${selectedMonth}_${selectedRun}`;

  // Load entity mappings on mount
  useEffect(() => {
    const loadEntityMappings = async () => {
      if (!companyId) return;

      try {
        const mappingsRef = collection(db, `companies/${companyId}/entity_mappings`);
        const snapshot = await getDocs(mappingsRef);
        const mappings = snapshot.docs.map(doc => ({
          code: doc.id,
          officeName: doc.data().officeName || doc.id,
          legalEntityName: doc.data().legalEntityName || doc.id
        }));
        setEntityMappings(mappings);
      } catch (err) {
        console.error('Failed to load entity mappings:', err);
      } finally {
        setLoadingMappings(false);
      }
    };

    loadEntityMappings();
  }, [companyId]);

  // Real-time run status listener
  useEffect(() => {
    if (!companyId) return;

    setLoadingStatus(true);
    const runRef = doc(db, `companies/${companyId}/multi_entity_runs/${runId}`);
    const unsub = onSnapshot(
      runRef,
      (snap) => {
        if (snap.exists()) {
          setRunStatus(snap.data() as MultiEntityUploadRunStatus);
        } else {
          setRunStatus(null);
        }
        setLoadingStatus(false);
      },
      () => {
        setRunStatus(null);
        setLoadingStatus(false);
      }
    );
    return () => unsub();
  }, [companyId, runId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (!selectedFile.name.toLowerCase().endsWith('.zip')) {
        setError('Please select a ZIP file containing entity commission files');
        return;
      }
      setFile(selectedFile);
      setError('');
      setMessage('');
    }
  };

  const handleUpload = async () => {
    if (!file || !companyId) {
      setError('Please select a ZIP file to upload');
      return;
    }

    setUploading(true);
    setError('');
    setMessage('Uploading ZIP file...');

    try {
      // Upload ZIP to staging area
      const timestamp = Date.now();
      const storagePath = `staging/${companyId}/multi_entity/${runId}_${timestamp}.zip`;
      const storageRef = ref(storage, storagePath);

      await uploadBytes(storageRef, file);
      setMessage('Processing multi-entity upload...');

      // Call the processMultiEntityUpload function
      const processUpload = httpsCallable(functions, 'processMultiEntityUpload');
      const result = await processUpload({
        zipStoragePath: storagePath,
        month: selectedMonth,
        runNumber: selectedRun
      });

      const data = result.data as {
        runId: string;
        entitiesFound: string[];
        entitiesProcessed: string[];
        entitiesMissingMapping: string[];
        filesQueued: number;
        errors: Array<{ file: string; error: string }>;
      };

      if (data.errors.length > 0) {
        setMessage(
          `Processed ${data.filesQueued} files for ${data.entitiesProcessed.length} entities. ` +
          `${data.errors.length} file(s) had errors.`
        );
      } else {
        setMessage(
          `Successfully queued ${data.filesQueued} files for ${data.entitiesProcessed.length} entities.`
        );
      }

      if (data.entitiesMissingMapping.length > 0) {
        setError(
          `Warning: No mapping found for entities: ${data.entitiesMissingMapping.join(', ')}. ` +
          `Please configure entity mappings in Settings.`
        );
      }

      // Status will update automatically via onSnapshot listener

      // Reset file
      setFile(null);
      const fileInput = document.getElementById('multi-entity-file') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Upload failed: ${errorMessage}`);
      setMessage('');
    } finally {
      setUploading(false);
    }
  };

  const getStatusColor = (status: EntityStatus['status']) => {
    switch (status) {
      case 'complete':
        return 'bg-green-500';
      case 'processing':
      case 'uploading':
        return 'bg-yellow-500 animate-pulse';
      case 'failed':
        return 'bg-red-500';
      default:
        return 'bg-gray-600';
    }
  };

  const getStatusLabel = (status: EntityStatus['status']) => {
    switch (status) {
      case 'complete':
        return 'Complete';
      case 'processing':
        return 'Processing';
      case 'uploading':
        return 'Uploading';
      case 'failed':
        return 'Failed';
      default:
        return 'Pending';
    }
  };

  // Generate month options (last 12 months)
  const monthOptions = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
    monthOptions.push({ value, label });
  }

  // Use entities from: 1) runStatus (server), 2) entityMappings (Firestore), 3) props
  const serverEntities = runStatus?.expectedEntities ?? [];
  const mappingEntities = entityMappings.map(m => m.code);
  const displayEntities = serverEntities.length > 0
    ? serverEntities
    : mappingEntities.length > 0
    ? mappingEntities
    : expectedEntities;

  // Build entity names and display labels from mappings
  const mappedEntityNames: Record<string, string> = {};
  const mappedDisplayNames: Record<string, string> = {};
  entityMappings.forEach(m => {
    mappedEntityNames[m.code] = m.officeName;
    mappedDisplayNames[m.code] = m.legalEntityName;
  });
  const completedCount = runStatus?.completedEntities?.length || 0;
  const totalEntities = displayEntities.length;
  const progressPercent = totalEntities > 0 ? Math.round((completedCount / totalEntities) * 100) : 0;

  return (
    <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
      <h2 className="text-xl font-bold mb-6">Multi-Entity Upload</h2>

      {/* Run Selector */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Commission Period
          </label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {monthOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Run Number
          </label>
          <select
            value={selectedRun}
            onChange={(e) => setSelectedRun(Number(e.target.value) as 1 | 2 | 3)}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={1}>RUN 1</option>
            <option value={2}>RUN 2</option>
            <option value={3}>RUN 3</option>
          </select>
        </div>
      </div>

      {/* Entity Status Grid */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-gray-300">Entity Status</h3>
          {runStatus && (
            <span className="text-sm text-gray-400">
              {completedCount}/{totalEntities} complete ({progressPercent}%)
            </span>
          )}
        </div>

        {/* Progress bar */}
        {runStatus && (
          <div className="w-full bg-gray-700 rounded-full h-2 mb-4">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          {loadingMappings ? (
            <div className="col-span-3 text-center py-4 text-gray-400">
              Loading entity mappings...
            </div>
          ) : displayEntities.length === 0 ? (
            <div className="col-span-3 text-center py-4 text-gray-500">
              No entity mappings configured. Go to Settings → Entity Mappings to set up.
            </div>
          ) : displayEntities.map((entityCode) => {
            const status = runStatus?.entityStatus?.[entityCode];
            const entityName = mappedEntityNames[entityCode] || entityNames[entityCode] || entityCode;

            return (
              <div
                key={entityCode}
                className="bg-gray-700/50 border border-gray-600 rounded-lg p-3"
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="font-mono text-sm text-gray-300 truncate min-w-0" title={mappedDisplayNames[entityCode] || entityCode}>{mappedDisplayNames[entityCode] || entityCode}</span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${getStatusColor(
                      status?.status || 'pending'
                    )} text-white`}
                  >
                    {getStatusLabel(status?.status || 'pending')}
                  </span>
                </div>
                <p className="text-xs text-gray-400 truncate" title={entityName}>
                  {entityName}
                </p>
                {status?.recordCount && (
                  <p className="text-xs text-gray-500 mt-1">
                    {status.recordCount.toLocaleString()} records
                  </p>
                )}
                {status?.error && (
                  <p className="text-xs text-red-400 mt-1 truncate" title={status.error}>
                    {status.error}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-900/50 border border-red-600 text-red-300 p-3 rounded mb-4">
          {error}
        </div>
      )}
      {message && (
        <div className="bg-green-900/50 border border-green-600 text-green-300 p-3 rounded mb-4">
          {message}
        </div>
      )}

      {/* Upload Dropzone */}
      <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center hover:border-gray-500 transition-colors">
        <input
          type="file"
          id="multi-entity-file"
          className="hidden"
          onChange={handleFileChange}
          accept=".zip"
        />
        <label htmlFor="multi-entity-file" className="cursor-pointer">
          <div className="flex flex-col items-center">
            <CloudUpload className="w-6 h-6" />
            {file ? (
              <div className="mt-4">
                <div className="bg-blue-900/40 border border-blue-500 rounded-lg p-3">
                  <p className="text-lg font-semibold text-white">{file.name}</p>
                  <p className="text-sm text-blue-300">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
            ) : (
              <>
                <p className="mt-2 text-sm text-gray-400">
                  Drop ZIP file here or click to select
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  ZIP should contain entity files (e.g., ENTO100 RUN 1 Nov 25.xlsx)
                </p>
              </>
            )}
          </div>
        </label>
      </div>

      {/* Upload Button */}
      <button
        onClick={handleUpload}
        disabled={!file || uploading}
        className={`mt-4 w-full font-medium py-3 px-6 rounded-lg transition-colors ${
          !file || uploading
            ? 'bg-gray-600 cursor-not-allowed text-gray-400'
            : 'bg-blue-600 hover:bg-blue-700 text-white'
        }`}
      >
        {uploading ? (
          <span className="flex items-center justify-center">
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Processing...
          </span>
        ) : (
          'Upload Multi-Entity ZIP'
        )}
      </button>

      {/* Help text */}
      <div className="mt-4 p-3 bg-gray-700/30 rounded-lg">
        <p className="text-xs text-gray-400">
          <strong>How it works:</strong> Upload a single ZIP containing all entity files for this
          run. VALUTTEN will automatically detect entity codes (e.g., ENTO100, ENTO101) from
          filenames and route each file to the correct office based on your entity mappings.
        </p>
      </div>
    </div>
  );
};

export default MultiEntityUploader;
